import FuseTreeViewModel from "../modelTreeViews/FuseTreeviewModel";
import BaseModel from "./BaseModel";
import type { ModelHeaderContext } from "./BaseModel";

import BaseObject from "./BaseObject";

import { copyAllProperties, sortArrayByProperty } from "./utils";

import Logger from "../Logger";

const logger = new Logger();
//////////////////////////////////////////////////
import ObjectFactory from "./ObjectFactory";
import Consumer from "./Consumer";
import ELCB from "./ELCB";
import { ChannelType } from "./ChannelType";
import Cabinet from "./Cabinet";

ObjectFactory.registerClass("Fuse", (data?: any, parent?: BaseModel) => {
  return new Fuse({});
});
//////////////////////////////////////////////////
export default class Fuse extends BaseModel {
  public get className(): string {
    return "Fuse"; // no itx
  }

  public get isFuse(): boolean {
    return true;
  }

  override headerTag(context: ModelHeaderContext): string {
    const parent = this.parent;
    if (parent?.className !== "ELCB" || !context.useDinConvention) {
      return super.headerTag(context);
    }

    const parentNumber = parent.tag.match(/\d+/)?.[0];
    const fuseNumber = this.tag.match(/\d+(?!.*\d)/)?.[0];
    if (!parentNumber || !fuseNumber) return super.headerTag(context);

    const siblingFuses = (parent as ELCB).fuses.sortBy("tag");
    let localFuseNumber = 1;
    for (const fuse of siblingFuses) {
      if (fuse === this) break;
      localFuseNumber += fuse.tagSize;
    }

    const dinPrefix = "-";
    switch (state.settings.fuseTagSchema) {
      case "A":
        return `${dinPrefix}${parent.tag}.${this.tag}`;
      case "B":
        return `${dinPrefix}${parentNumber}${state.settings.devicePrefix.fuse}${localFuseNumber}`;
      case "C":
        return `${dinPrefix}${state.settings.devicePrefix.elcb}${parentNumber}.${localFuseNumber}`;
    }
  }

  _installationsStatus = new Map<string, boolean>();

  // -----------------------------------------------------------------------------
  // is_FI_LS
  // -----------------------------------------------------------------------------
  _is_FI_LS?: boolean;
  get is_FI_LS(): boolean | undefined {
    return this._is_FI_LS;
  }

  set is_FI_LS(newValue: boolean | undefined) {
    if (this._is_FI_LS === newValue) return;
    this.beforeChange("is_FI_LS");
    this._is_FI_LS = newValue;
    this.changed("is_FI_LS");
  }

  // -----------------------------------------------------------------------------

  setInstallationsStatus(consumerId: string, status: boolean) {
    this._installationsStatus.set(consumerId, status);
  }

  getInstallationsStatus(consumerId: string): boolean | undefined {
    const res = this._installationsStatus.get(consumerId);
    if (res !== undefined) {
      return res;
    }
    if (!this.consumers.some((consumer) => consumer.id === consumerId)) return undefined;
    this._installationsStatus.set(consumerId, false);
    return false;
  }

  getPersistentProperties(): string[] {
    return ["installationsStatus", "is_FI_LS", ...super.getPersistentProperties()]; // no itx
  }

  delete(): Array<BaseObject> {
    this.isDeleting = true;

    var res = new Array<BaseObject>();
    // remove all connections to consumers
    this.consumers.forEach((consumer) => {
      consumer.fuseId = "";
      consumer.raiseOnChange("fuse");
    });

    res = res.concat(super.delete());
    return res;
  }

  //
  // backpointer collection to consumers
  // diese collection wird automtisch gepflegt
  // durch die RelationConsumerToFuse in Consumer
  //
  // NIEMALS direkt beschreiben oder löschen
  //
  consumers = new Array<Consumer>();

  get maxTagCharacters(): number {
    return 6; // for fuse max tag 6 because of consumer with 3 fuses( F11-13)
  }

  emptyTagIsForbidden(): boolean {
    return true;
  }

  canBeEditedInteractive(): boolean {
    return false;
  }

  canBeMovedToNewParent(newParent: BaseModel) {
    // in same cabinet possible if not forbidden
    return newParent.parent == this.parent?.parent;
  }

  getParentChildrenGroup(): Array<BaseModel> | undefined {
    return (this.parent as ELCB)?.fuses.sortBy("tag");
  }
  getAllTagsFromSameTypeWithoutOwn(): string[] | undefined {
    const parentELCB = this.parent as ELCB;
    if (parentELCB && !state.projectMetadata.uniqueTagsForProject) {
      const res = new Array<string>();
      parentELCB.fuses.forEach((fuse1) => {
        if (this.tag != fuse1.tag) {
          res.push(fuse1.tag);
        }
      });
      return res;
    } else if (state.projectMetadata.uniqueTagsForProject) {
      const res = new Array<string>();
      state.allELCBs().forEach((elcb1) => {
        if (this.tag != elcb1.tag) {
          res.push(elcb1.tag);
        }
      });
      state.allFuses().forEach((fuse1) => {
        if (this.tag != fuse1.tag) {
          res.push(fuse1.tag);
        }
      });

      return res;
    }
    return undefined;
  }
  getSize(): number {
    // tags under an elcb are "<elcbTag>.<localPart>"; only the local part encodes the pole range/size
    const elcbTag = this.parent?.className == "ELCB" ? (this.parent as ELCB).tag : undefined;
    const localTag = elcbTag && this.tag.startsWith(`${elcbTag}.`) ? this.tag.substring(elcbTag.length + 1) : this.tag;

    const splittedViaMinus = localTag.split("-");
    if (splittedViaMinus.length == 1) {
      return localTag.split(",").length;
    } else {
      const leftnum = parseInt(splittedViaMinus[0].replace(/^\D+/g, ""));
      const rightnum = parseInt(splittedViaMinus[splittedViaMinus.length - 1].replace(/^\D+/g, ""));
      if (Number.isNaN(leftnum) || Number.isNaN(rightnum)) {
        return 1;
      }
      return rightnum - leftnum + 1;
    }
  }

  constructor(data?: any, parent?: BaseObject) {
    super(data, parent);
    copyAllProperties(data, this);
    this.treeViewModel = new FuseTreeViewModel(this);
  }

  isConnectable(consumer?: Consumer): boolean {
    let res = true;
    // 400v consumer only allowed if no other consumer is connected to the fuse
    if (consumer?.channelType == ChannelType.suply400V && this.consumers.length != 0) {
      return false;
    }

    // if already a 400v consumer is connected no other consumer is allowed
    this.consumers.forEach((consumer) => {
      if (consumer.channelType == ChannelType.suply400V) {
        res = false;
      }
    });

    // Lock consumer not allowed
    if (consumer?.channelType == ChannelType.Lock) {
      return false;
    }

    return res;
  }

  getParentCollectionName(): string | undefined {
    return "fuses"; // no itx
  }

  
}
